import { DollarSign, Search, Filter, CheckCircle2, Clock, XCircle, X, QrCode } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import RecordPaymentView from './RecordPaymentView';
import API_BASE_URL from '../../config';
import UPIPaymentModal from './UPIPaymentModal';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const PAGE_SIZE = 10;

const getTodayDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PaymentsTab = () => {
  const location = useLocation();
  const initialDateFilter = location.state?.dateFilter || 'all';
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [timeFilter, setTimeFilter] = useState('monthly');
  const [graphData, setGraphData] = useState([]);
  const [page, setPage] = useState(0);

  // Advanced Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterMethod, setFilterMethod] = useState('');

  // Derived options for Plan and PaymentMethod dropdowns
  const planOptions = Array.from(new Set(payments.map(p => p.plan || '').filter(Boolean)));
  const methodOptions = Array.from(new Set(payments.map(p => p.paymentMethod || '').filter(Boolean)));
  const [showUPIModal, setShowUPIModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [bannerMessage, setBannerMessage] = useState('');
  const [bannerType, setBannerType] = useState(''); // 'success' or 'error'
  const showBanner = (message, type) => {
    setBannerMessage(message);
    setBannerType(type);
    setTimeout(() => {
      setBannerMessage('');
      setBannerType('');
    }, type === 'success' ? 3000 : 5000);
  };

  const handleUPIPaymentReceived = async () => {
    if (!selectedPayment) return;

    try {
      const paymentData = {
        PaymentId: selectedPayment.id,             // update this record
        UserId: selectedPayment.UserId || '', // if you store it
        PlanId: String(selectedPayment.plan),
        Amount: selectedPayment.amount,
        PaymentStatus: 'Paid',
        Gymcode: JSON.parse(localStorage.getItem('fitscan_user'))?.gymcode,
        PaymentMethod: 'UPI',
        PaidOn: getTodayDate(),
        TransactionId: selectedPayment.transactionId || `OAReg-${selectedPayment.id}-${Date.now()}`,
        Notes: 'Payment completed via UPI QR',
        UpdatedBy: JSON.parse(localStorage.getItem('fitscan_user'))?.username || ''
      };

      await axios.put(`${API_BASE_URL}/api/payment/update`, paymentData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        }
      });

      showBanner('Payment recorded successfully!', 'success');

      // Refresh table + close modal
      await fetchGymPayments();
      setShowUPIModal(false);
      setSelectedPayment(null);
    } catch (error) {
      console.error('Payment recording failed:', error);
      showBanner('Payment recording failed. Please try again.', 'error');
    }
  };

  const handleUPIPayLater = async () => {
    if (!selectedPayment) return;

    try {
      const pendingPaymentData = {
        PaymentId: selectedPayment.id,
        UserId: selectedPayment.UserId || '',
        PlanId: String(selectedPayment.plan),
        Amount: selectedPayment.amount,
        PaymentStatus: 'Pending',
        Gymcode: JSON.parse(localStorage.getItem('fitscan_user'))?.gymcode,
        PaymentMethod: 'UPI-Pending',
        PaidOn: selectedPayment.date || getTodayDate(),
        TransactionId: selectedPayment.transactionId || `PENDING-${selectedPayment.id}-${Date.now()}`,
        Notes: 'Payment pending via UPI QR',
        UpdatedBy: JSON.parse(localStorage.getItem('fitscan_user'))?.username || ''
      };

      await axios.put(`${API_BASE_URL}/api/payment/update`, pendingPaymentData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        }
      });

      showBanner('Payment marked as pending. User can pay later.', 'success');
      await fetchGymPayments();
    } catch (error) {
      console.error('Pending payment update failed:', error);
    } finally {
      setShowUPIModal(false);
      setSelectedPayment(null);
    }
  };


  // Fetch payments on mount or when needed
  const fetchGymPayments = async () => {
    const userInfo = JSON.parse(localStorage.getItem('fitscan_user'));

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/payment/fetchdetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify(userInfo.gymcode),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const paymentdetails = data.map((u) => ({
        id: u.paymentId,
        member: `${u.firstName} ${u.lastName}`,
        UserId: u.userId,
        plan: u.planId,
        amount: u.amount,
        status: (u.paymentStatus || '').toLowerCase(),
        date: u.paidOn?.split('T')[0],
        paymentMethod: u.paymentMethod,
        transactionId: u.transactionId,
        notes: u.notes,
      }));

      setPayments(paymentdetails);
      setError('');
      setPage(0); // Reset to first page on reload
    } catch (err) {
      console.error(err);
      setError('Failed to load data');
      setPayments([]);
      setGraphData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGymPayments();
  }, []);

  useEffect(() => {
    if (payments.length === 0) {
      setGraphData([]);
      return;
    }
    const grouped = {};

    payments
      .filter(p => p.status === 'paid')
      .forEach(p => {
        if (!p.date) return;
        const date = new Date(p.date);
        let key = '';
        if (timeFilter === 'weekly') {
          const weekDate = new Date(date);
          weekDate.setDate(weekDate.getDate() - weekDate.getDay());
          key = weekDate.toISOString().split('T')[0];
        } else if (timeFilter === 'monthly') {
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        } else if (timeFilter === 'yearly') {
          key = `${date.getFullYear()}`;
        }
        if (!grouped[key]) grouped[key] = 0;
        grouped[key] += p.amount;
      });

    const result = Object.entries(grouped).map(([key, value]) => ({
      time: key,
      amount: value
    })).sort((a, b) => a.time.localeCompare(b.time));

    setGraphData(result);
  }, [payments, timeFilter]);

  // Filtering payments by status, then by advanced filter
  const filteredPayments = payments.filter(payment => {
    // Date Filters
    if (dateFilter === 'today') {
      const todayStr = getTodayDate();
      if (payment.date !== todayStr) return false;
    } else if (dateFilter === 'month') {
      const currentMonthStr = getTodayDate().substring(0, 7); // YYYY-MM
      if (!payment.date || !payment.date.startsWith(currentMonthStr)) return false;
    }
    // Status chips
    if (activeFilter !== 'all' && payment.status !== activeFilter) return false;
    if (filterFrom && (!payment.date || payment.date < filterFrom)) return false;
    if (filterTo && (!payment.date || payment.date > filterTo)) return false;
    if (filterPlan && payment.plan !== filterPlan) return false;
    if (filterMethod && payment.paymentMethod !== filterMethod) return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (payment.member && payment.member.toLowerCase().includes(query)) ||
        (payment.id && String(payment.id).toLowerCase().includes(query)) ||
        (payment.plan && String(payment.plan).toLowerCase().includes(query)) ||
        (payment.amount && String(payment.amount).toLowerCase().includes(query)) ||
        (payment.date && payment.date.toLowerCase().includes(query)) ||
        (payment.paymentMethod && payment.paymentMethod.toLowerCase().includes(query)) ||
        (payment.transactionId && String(payment.transactionId).toLowerCase().includes(query)) ||
        (payment.status && payment.status.toLowerCase().includes(query)) ||
        (payment.notes && payment.notes.toLowerCase().includes(query))
      );
    }
    return true;
  });




  const formatXAxis = (tickItem) => {
    // Only format for monthly view to show month names
    if (timeFilter === 'monthly' && tickItem.includes('-')) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const [year, month] = tickItem.split('-');
      const monthIndex = parseInt(month, 10) - 1;

      if (monthIndex >= 0 && monthIndex < 12) {
        return `${year}-${monthNames[monthIndex]}`;
      }
    }
    // Return original for weekly/yearly or if format is unexpected
    return tickItem;
  };
  const total = filteredPayments.length;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const pagedPayments = filteredPayments.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // --- CALCULATE MONTHLY REVENUE FOR "THIS MONTH" ---
  function getCurrentMonthRevenue() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    return payments
      .filter(p => p.status === 'paid' && p.date && (() => {
        const dt = new Date(p.date);
        return dt.getFullYear() === currentYear && dt.getMonth() === currentMonth;
      })())
      .reduce((sum, payment) => sum + payment.amount, 0);
  }

  // ------ FILTERS MODAL ------
  const FiltersModal = () => {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
        aria-modal="true"
        tabIndex={-1}
        onClick={() => setShowFilters(false)}
      >
        <div
          className="bg-[#181818] text-gray-200 p-6 rounded-xl w-[90vw] max-w-md z-60 shadow-lg relative"
          onClick={e => e.stopPropagation()}
        >
          <button
            className="absolute right-2 top-2 text-gray-400 hover:text-red-400"
            onClick={() => setShowFilters(false)}
          >
            <X size={24} />
          </button>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Filter size={22} className="text-lime-400" />
            Advanced Filters
          </h2>
          <div className="space-y-4">
            {/* Date range */}
            <div>
              <label className="block mb-1 text-gray-300">Date From</label>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                className="w-full px-3 py-2 bg-[#222] rounded border border-[#2b2b2b]" />
            </div>
            <div>
              <label className="block mb-1 text-gray-300">Date To</label>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                className="w-full px-3 py-2 bg-[#222] rounded border border-[#2b2b2b]" />
            </div>
            {/* Plan dropdown */}
            <div>
              <label className="block mb-1 text-gray-300">Plan</label>
              <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}
                className="w-full px-3 py-2 bg-[#222] rounded border border-[#2b2b2b]">
                <option value="">All Plans</option>
                {planOptions.map(plan =>
                  <option key={plan} value={plan}>{plan}</option>
                )}
              </select>
            </div>
            {/* Payment method */}
            <div>
              <label className="block mb-1 text-gray-300">Payment Method</label>
              <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)}
                className="w-full px-3 py-2 bg-[#222] rounded border border-[#2b2b2b]">
                <option value="">All Methods</option>
                {methodOptions.map(meth =>
                  <option key={meth} value={meth}>{meth}</option>
                )}
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-6 gap-2">
            <button
              className="px-4 py-2 rounded bg-[#222] border border-[#2b2b2b] text-gray-400 hover:bg-[#2b2b2b]"
              onClick={() => {
                setFilterFrom('');
                setFilterTo('');
                setFilterPlan('');
                setFilterMethod('');
                setShowFilters(false);
                setPage(0);
              }}
            >Reset</button>
            <button
              className="px-4 py-2 rounded bg-lime-400 text-[#181f10] hover:bg-lime-300 font-semibold"
              onClick={() => {
                setShowFilters(false);
                setPage(0);
              }}
            >Apply</button>
          </div>
        </div>
      </div>
    );
  };

  // --- RENDER ---
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign size={24} className="text-lime-400" />
            Payments Management
          </h1>
          <p className="text-gray-400 mt-1">
            Total revenue this month:&nbsp;
            <span className="text-lime-400 font-medium">
              ₹{getCurrentMonthRevenue().toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </p>
        </div>

        {/* Search + Filter together */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400"
            />
          </div>

          <button
            className="flex items-center gap-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] px-3 py-2 rounded-lg whitespace-nowrap"
            onClick={() => setShowFilters(true)}
          >
            <Filter size={18} />
            <span>Filters</span>
          </button>
        </div>
      </div>


      {showFilters && <FiltersModal />}

      {/* Payments status filter */}
      <div className="flex flex-wrap gap-2 items-center pb-2">
        <div className="flex gap-1.5 bg-[#1a1a1a] p-1 rounded-full border border-[#2b2b2b]">
          {['all', 'paid', 'pending', 'failed'].map((filter) => (
            <button
              key={filter}
              onClick={() => {
                setActiveFilter(filter);
                setPage(0);
              }}
              className={`px-4 py-1.5 rounded-full capitalize text-xs whitespace-nowrap transition-all ${activeFilter === filter
                ? 'bg-lime-400 text-zinc-950 font-bold'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              {filter}
            </button>
          ))}
        </div>
        <div className="w-px h-6 bg-[#2b2b2b] hidden sm:block" />
        <div className="flex gap-1.5 bg-[#1a1a1a] p-1 rounded-full border border-[#2b2b2b]">
          {[
            { id: 'all', label: 'All Time' },
            { id: 'today', label: 'Today' },
            { id: 'month', label: 'This Month' }
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => {
                setDateFilter(filter.id);
                setPage(0);
              }}
              className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${dateFilter === filter.id
                ? 'bg-lime-400 text-zinc-950 font-bold'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notification Banner */}
      {bannerMessage && (
        <div className={`p-4 rounded-xl border-2 flex items-center gap-3 mb-4 animate-in slide-in-from-top-2 duration-300 ${bannerType === 'success'
          ? 'bg-lime-500/10 border-lime-400 text-lime-100'
          : 'bg-red-500/10 border-red-400 text-red-100'
          }`}>
          {bannerType === 'success' ? (
            <CheckCircle2 size={24} className="text-lime-400 flex-shrink-0" />
          ) : (
            <XCircle size={24} className="text-red-400 flex-shrink-0" />
          )}
          <span className="font-medium">{bannerMessage}</span>
          <button
            onClick={() => {
              setBannerMessage('');
              setBannerType('');
            }}
            className="ml-auto p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      )}


      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading payments...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-4">{error}</div>
      ) : (
        <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Table header */}
              <div className="grid grid-cols-12 bg-[#222] px-2 py-2 font-medium text-sm">
                <div className="col-span-3">Member</div>
                <div className="col-span-1">Plan</div>
                <div className="col-span-2">Amount</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1 text-center">UPI</div>
                <div className="col-span-1">Payment ID</div>
              </div>
              {/* Rows */}
              <div className="max-h-[350px] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#84cc16 #181818' }}>
                {pagedPayments.length === 0 && (
                  <div className="p-8 text-center text-gray-400">
                    No payment records for this filter.
                  </div>
                )}
                {pagedPayments.map((payment) => (
                  <div key={payment.id} className="grid grid-cols-12 px-2 py-2 border-b border-[#2b2b2b] hover:bg-[#222] items-center text-sm">
                    <div className="col-span-3">{payment.member}</div>
                    <div className="col-span-1">
                      <span className="px-1 py-0.5 rounded-full text-xs bg-amber-400/10 text-amber-400 whitespace-nowrap">
                        {payment.plan}
                      </span>
                    </div>
                    <div className="col-span-2">
                      ₹{payment.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="col-span-2 text-gray-400">{payment.date}</div>

                    {/* Status chip */}
                    <div className="col-span-2">
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${payment.status === 'paid'
                        ? 'bg-lime-400/10 text-lime-400'
                        : payment.status === 'pending'
                          ? 'bg-red-400/10 text-red-400'
                          : 'bg-amber-400/10 text-amber-400'
                        }`}>
                        {payment.status === 'paid' ? <CheckCircle2 size={14} /> :
                          payment.status === 'pending' ? <Clock size={14} /> :
                            <XCircle size={14} />}
                        {payment.status}
                      </span>
                    </div>

                    {/* QR icon (only for pending) */}
                    <div className="col-span-1 flex justify-center">
                      {payment.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowUPIModal(true);
                          }}
                          className="p-2 rounded-full hover:bg-[#2b2b2b] text-lime-400"
                          title="Open UPI QR"
                        >
                          <QrCode size={18} />
                        </button>
                      )}
                    </div>

                    {/* Payment ID — now LAST */}
                    <div className="col-span-1 font-mono text-xs text-gray-400">
                      {payment.id}
                    </div>
                  </div>

                ))}
              </div>
            </div>
          </div>
          {/* PAGINATION */}
          <div className="flex justify-between items-center px-4 py-2 border-t border-[#222] bg-[#181818]">
            {/* <button className="flex items-center gap-2 text-gray-400 hover:text-lime-400">
              <Download size={16} />
              Export Payment History
            </button> */}
            <div className="flex gap-2 items-center">
              <button
                className="px-3 py-1 bg-[#222] border border-[#2b2b2b] rounded hover:bg-[#2b2b2b] disabled:opacity-50"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span className="text-gray-400 text-sm">
                Page {page + 1} of {maxPage + 1}
              </span>
              <button
                className="px-3 py-1 bg-lime-400 text-[#181f10] rounded hover:bg-lime-300 disabled:opacity-50"
                onClick={() => setPage(p => Math.min(maxPage, p + 1))}
                disabled={page >= maxPage}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GRAPH: scrollable and wide */}
      <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-white">Payment Trends</h2>
          <select value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="bg-[#222] text-white border border-[#2b2b2b] rounded px-2 py-1">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        {/* Horizontal scrollable graph */}
        {graphData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={graphData}
              barSize={Math.max(20, Math.min(60, Math.floor(300 / (graphData.length || 1))))}
              barCategoryGap="30%"
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid stroke="#2b2b2b" />
              <XAxis
                dataKey="time"
                stroke="#aaa"
                tickFormatter={formatXAxis}
                tick={{ fontSize: 11 }}
                padding={{ left: 20, right: 20 }}
              />
              <YAxis stroke="#aaa" allowDecimals={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#222', borderColor: '#2b2b2b', color: '#fff' }}
              />
              <Bar dataKey="amount" fill="#84cc16" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-gray-400 text-sm">No payment data available for selected filter.</div>
        )}
      </div>

      {showRecordPayment && (
        <RecordPaymentView
          onClose={() => setShowRecordPayment(false)}
          onRecordPayment={(paymentData) => {
            fetchGymPayments();
            setShowRecordPayment(false);
          }}
        />
      )}

      {showUPIModal && selectedPayment && (
        <UPIPaymentModal
          isOpen={showUPIModal}
          onClose={() => {
            setShowUPIModal(false);
            setSelectedPayment(null);
          }}
          onPaymentReceived={handleUPIPaymentReceived}
          onPayLater={handleUPIPayLater}
          amount={selectedPayment.amount}
          gymName={JSON.parse(localStorage.getItem('fitscan_user'))?.gymname || 'FitScan Gym'}
          message="Please complete payment via UPI scan"
          transactionId={selectedPayment.transactionId}
        />
      )}


    </div>
  );
};

export default PaymentsTab;
